import { useEffect, useState, useMemo, useRef } from 'react';
import {
  useCountry,
  useSettings,
  useLocalizedVariant,
  useCartAddItem,
  useProductInventoryByHandle,
  useRouter,
} from '@backpackjs/storefront';
import DarkCircleCheckmark from 'src/snippets/icons/DarkCircleCheckmark';
import { YotpoScript } from '@/utils/YotpoScript';
import { useWindowDimensions, getDiscountedPrice } from "@/utils/Helper";

import AfterPay from '@/snippets/products/AfterPay';
import ProductGallery from '@/snippets/products/ProductGallery';
import ProductInformation from '@/snippets/products/ProductInformation';
import BackInStockModal from '@/snippets/products/BackInStockModal';
import SubscriptionBenefitsModal from '@/snippets/products/SubscriptionBenefitsModal';
import { getMetafieldMap } from '@/utils/Metafields';
import BackToTop from '@/snippets/components/BackToTop';
import ProductSticky from '@/snippets/products/ProductSticky';
import { useDataLayerActions } from '@hooks';
import ProductEmailSignup from '@/snippets/globals/layout/ProductEmailSignup';
import ReviewsWrapper from '@/snippets/products/ReviewsWrapper';

export default function ProductTemplate({ renderSections, product }) {
  const settings = useSettings();
  const { sendViewProductEvent } = useDataLayerActions();
  const { country } = useCountry();
  const countryCode = country?.isoCode || 'US';
  const isUs = countryCode === 'US';
  const { cartAddItem } = useCartAddItem();
  const variant = useGetInitialVariant(product);
  const { inventory, ...inventoryStatus } = useProductInventoryByHandle({
    handle: product.handle,
  });
  const metafieldMap = useMemo(() => {
    return getMetafieldMap(product);
  }, [product?.id]);
  const { localized: localizedVariant } = useLocalizedVariant({ variant });
  const selectedVariant = { ...variant, ...localizedVariant };
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const firstUpsellProduct = metafieldMap.product_upsell_1;
  const buyboxRef = useRef(null);
  const productInfoRef = useRef(null);
  const isComingSoon = metafieldMap.coming_soon;
  const router = useRouter();
  const { vpWidth } = useWindowDimensions();
  const comparePriceText = metafieldMap?.custom_compare_at_price || '';

  if (
    typeof history !== 'undefined' &&
    typeof history.scrollRestoration !== 'undefined'
  ) {
    history.scrollRestoration = 'manual';
  }

  const selectedPlan = selectedVariant?.sellingPlanAllocations?.find(
    (plan) => plan?.sellingPlan.id === selectedPlanId && isUs
  );

  const firstPlan = selectedVariant?.sellingPlanAllocations?.[0];

  const planAmount = parseFloat(
    selectedPlan?.priceAdjustments?.[0]?.price?.amount ||
      firstPlan?.priceAdjustments?.[0]?.price?.amount
  );
  const variantAmount = selectedVariant?.priceV2?.amount;
  let compareAmount = selectedVariant?.compareAtPriceV2?.amount;

  const isLocalized = !!selectedVariant?.priceV2?.locale;
  const currencySymbol = isLocalized ? country?.currency?.symbol || '$' : '$';
  const currencyCode = selectedVariant?.priceV2?.currencyCode || 'USD';

  let sellingPlanPrice = planAmount
    ? `${currencySymbol}${planAmount.toFixed(2)}`.replace('.00', '')
    : null;
  let comparePrice = compareAmount
    ? `${currencySymbol}${compareAmount}`.replace('.00', '')
    : null;
  let variantPrice = `${currencySymbol}${variantAmount}`.replace('.00', '');

  const planOptions = selectedVariant?.sellingPlanAllocations?.map((plan) => ({
    label: plan?.sellingPlan?.name?.replace(/Delivery/i, ''),
    value: plan?.sellingPlan?.id,
  }));

  useEffect(() => {
    if (settings?.subscriptions?.selectSubscriptionOnLoad) {
      setSelectedPlanId(selectedVariant?.sellingPlanAllocations?.[0]?.sellingPlan?.id)
    }
  }, [])


  useEffect(() => {
    if (metafieldMap.display_custom_cursor === 'true') {
      document.body.classList.add('show-custom-cursor');
    } else {
      document.body.classList.remove('show-custom-cursor');
    }
  }, [])

  useEffect(() => {
    if (!selectedVariant) return;
    const { search } = window.location;
    const searchParams = new URLSearchParams(search);
    let planId = searchParams.get('selling_plan');
    const defaultSubscription = searchParams.has('subscription');
    let activePlan = selectedVariant.sellingPlanAllocations?.find((plan) => {
      return plan.sellingPlan.id.includes(planId);
    });

    if (!activePlan && defaultSubscription) {
      activePlan = selectedVariant.sellingPlanAllocations?.[0]?.sellingPlan;
      planId = selectedVariant.sellingPlanAllocations?.[0]?.sellingPlan.id;

      delete router.query.subscription;

      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
          },
        },
        undefined,
        { shallow: true }
      );
    }

    if (activePlan) {
      setSelectedPlanId(planId);
    }
  }, [selectedVariant]);

  const [productsizes, productupsells, videowise, sections] = useMemo(() => {
    const _sections = renderSections();
    if (!_sections) return [null, []];

    const productSizesSection = _sections.filter(
      (s) => s.props['data-comp'] === 'productsizes'
    );
    const productUpsellsSection = _sections.filter(
      (s) => {
        return s.props['data-comp'] === 'productupsells' && (vpWidth >= 1024)
      }
    );
    const videoWiseSection = _sections.filter(
      (s) => s.props['data-comp'] === 'VideoWise'
    );
    const otherSections = _sections.filter(
      (s) => {
        if (vpWidth < 1024) {
          return !['productsizes'].includes(s.props['data-comp']);
        }
        return !['productsizes', 'productupsells'].includes(s.props['data-comp']);
      }
    );
    return [productSizesSection, productUpsellsSection, videoWiseSection, otherSections];
  }, [renderSections, vpWidth]);

  const tracksInventory = product?.tracksInventory;
  const hasInventory = !tracksInventory || (inventory?.totalInventory || 0) > 0;

  const toggleBackInStockModal = (e) => {
    e.preventDefault();
    document.dispatchEvent(new CustomEvent('backInStockModal:open'));
  };

  const toggleSubscriptionModal = (e) => {
    e.preventDefault();
    document.dispatchEvent(new CustomEvent('subscriptionModal:open'));
  }

  const variantIsAvailable =
    (inventory?.variants?.[selectedVariant?.id]?.availableForSale &&
      hasInventory) ||
    !tracksInventory;

  const jsonSchema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.title,
    image: product.images.map((image) => image.src),
    description: product.description,
    brand: {
      '@type': 'Brand',
      name: product.vendor.replace('_', ' '),
    },
    offers: product.variants.map((variant) => {
      return {
        '@type': 'Offer',
        url: product.onlineStoreUrl,
        priceCurrency: variant.currencyCode,
        price: variant.price,
        itemCondition: 'https://schema.org/NewCondition',
        availability: `https://schema.org/${
          variant.availableForSale ? 'InStock' : 'OutOfStock'
        }`,
      };
    }),
  };
  useMemo(() => {
    YotpoScript();
  }, [product.id]);

  useEffect(() => {
    sendViewProductEvent({ product, selectedVariant: variant });
  }, [variant?.product?.handle, sendViewProductEvent]);

  const discountedPrice = getDiscountedPrice(settings?.automaticDiscountCollections, product, selectedVariant, currencySymbol, planAmount);

  if (discountedPrice?.productPrice && discountedPrice?.comparePrice) {
    variantPrice = discountedPrice.productPrice;
    compareAmount = discountedPrice.comparePrice;
    comparePrice = discountedPrice.comparePrice;

    if (sellingPlanPrice && discountedPrice?.sellingPlanPrice) {
      sellingPlanPrice = discountedPrice.sellingPlanPrice;
    }
  }

  return (
    <div
      data-comp="ProductTemplate"
      id={product?.legacyResourceId}
      key={product?.legacyResourceId}
    >
      <div
        className="product-main-container flex w-full flex-col lg:container lg:mx-auto lg:flex-row"
        ref={buyboxRef}
      >
        <div className="product-gallery mb-4 flex w-full flex-shrink-0 lg:mb-0 lg:w-7/12">
          <ProductGallery key={product.legacyResourceId} product={product} />
        </div>

        <div
          className="product-info-container m-auto w-full lg:m-0 lg:w-5/12"
          ref={productInfoRef}
        >
          <div className="product-info-inner-container m-auto max-w-[642px] p-6 pt-1 lg:m-0 lg:p-12 lg:pt-6 lg:pr-8 lg:pb-0 xl:pr-12">
            <ProductInformation
              product={product}
              selectedVariant={selectedVariant}
              selectedPlan={selectedPlan}
            />
            {productsizes}

            <div className="block w-full">
              <ProductOptions
                product={product}
                activeVariant={selectedVariant}
              />
              {variantIsAvailable &&
                country &&
                isUs &&
                selectedVariant?.sellingPlanAllocations &&
                selectedVariant?.sellingPlanAllocations?.length > 0 && (
                  <div className="mb-[16px] overflow-hidden border">
                    <button
                      type="button"
                      className={`one-time-option grid w-full grid-cols-[auto_auto_1fr] border-b bg-[#F5F5F5] px-[12px] py-[15px] md:px-[20px]  ${
                        !selectedPlanId && 'bg-[#FFFFFF]'
                      }`}
                      onClick={() => {
                        setSelectedPlanId(null);
                        window.dataLayer.push({
                          event: 'PDP - One time select',
                        });
                      }}
                    >
                      <div className="relative top-[2px] mr-[10px] flex h-[18px] w-[18px] cursor-pointer rounded-full border md:top-0 md:mr-[12px] md:h-[20px] md:w-[20px]">
                        {!selectedPlanId && (
                          <div className="relative top-[2px] left-[2px] h-[12px] w-[12px] rounded-full bg-black md:h-[14px] md:w-[14px]" />
                        )}
                      </div>
                      <p className="mb-0 font-hafferBold tracking-wide text-[14px]">
                        One-time purchase
                      </p>
                      <div className="ml-auto font-hafferBold tracking-wide text-[14px]">
                        {comparePrice && (
                          <>
                            <span className="product-original-price mr-2 font-hafferBold tracking-wide text-[14px] text-[#757575] line-through">
                              {`${comparePrice}`}
                            </span>
                            <span className="sr-only">
                              Previous price {`${comparePrice}`}
                            </span>
                          </>
                        )}
                        <span>{`${variantPrice}`}</span>
                        <span className="sr-only">
                          Current price {`${variantPrice}`}
                        </span>
                      </div>
                    </button>
                    <div
                      className={`${
                        selectedPlanId ? 'bg-[#FFFFFF]' : 'bg-[#F5F5F5]'
                      }`}
                    >
                      <button
                        type="button"
                        className="subscribe-and-save-option grid w-full grid-cols-[auto_auto_1fr] px-[12px] py-[15px] md:px-[20px]"
                        onClick={() => {
                          setSelectedPlanId(firstPlan?.sellingPlan?.id);
                          window.dataLayer.push({
                            event: 'PDP - Subscribe & Save select',
                          });
                        }}
                      >
                        <span className="relative top-[2px] mr-[10px] flex h-[18px] w-[18px] cursor-pointer rounded-full border md:top-0 md:mr-[12px] md:h-[20px] md:w-[20px]">
                          {selectedPlanId && (
                            <div className="relative top-[2px] left-[2px] h-[12px] w-[12px] rounded-full bg-black md:h-[14px] md:w-[14px]" />
                          )}
                        </span>
                        <div className="text-left">
                          <p className="mb-0 font-hafferBold tracking-wide text-[14px]">
                            <span className="whitespace-nowrap">
                              {firstPlan?.sellingPlan?.priceAdjustments[0]
                                ?.adjustmentValue
                                ? `Subscribe & save ${firstPlan?.sellingPlan?.priceAdjustments[0].adjustmentValue?.adjustmentPercentage}%`
                                : `Subscribe`}
                            </span>

                            {settings?.subscriptions?.freeShippingText && (
                              <span className="desktop-free-shipping-text ml-[20px] hidden whitespace-nowrap bg-[#E61A4F] px-[7px] py-[4px] text-[11px] text-white xl:inline">
                                {settings?.subscriptions?.freeShippingText}
                              </span>
                            )}
                          </p>
                          {settings?.subscriptions?.freeShippingText && (
                            <span className="mobile-free-shipping-text whitespace-nowrap bg-[#E61A4F] px-[7px] py-[4px] font-hafferBold tracking-wide text-[11px] text-white xl:hidden">
                              {settings?.subscriptions?.freeShippingText}
                            </span>
                          )}
                        </div>
                        {sellingPlanPrice ? (
                          <div className="ml-auto">
                            <span className="product-original-price ml-4 font-hafferBold tracking-wide text-[14px] text-[#757575] line-through">
                              {`${variantPrice}`}
                            </span>
                            <span className="sr-only">
                              Previous price {`${variantPrice}`}
                            </span>
                            <span className="ml-2 font-hafferBold tracking-wide text-[14px]">
                              {sellingPlanPrice}
                            </span>
                            <span className="sr-only">
                              Current price {`${sellingPlanPrice}`}
                            </span>
                          </div>
                        ) : (
                          <div className="ml-auto font-hafferBold tracking-wide text-[14px]">
                            <span className="sr-only">Subscription price</span>
                            {variantPrice}
                          </div>
                        )}
                        <p className="new-pdp-descriptor col-span-2 col-start-2 mb-0 hidden text-left text-[11px]">
                          <span className="mr-2 bg-primary-2 px-[7px] py-1 font-hafferBold tracking-wide text-white">
                            {settings?.subscriptions?.colorBadgeText ||
                              'Most Popular!'}
                          </span>
                          {
                            firstPlan?.sellingPlan?.priceAdjustments[0]
                              ?.adjustmentValue?.adjustmentPercentage
                          }
                          % off + Free Shipping
                        </p>
                      </button>
                      {selectedPlanId && (
                        <div className="px-[12px] pb-[15px] md:px-[20px]">
                          <div className="mb-[8px] flex items-end whitespace-nowrap">
                            <label
                              className="text-[12px] text-black"
                              htmlFor="selling-plan-select"
                            >
                              Ships every:
                            </label>
                            <div className="select-wrap-2 relative flex flex-col">
                              <select
                                className="ml-2 cursor-pointer border-0 border-b bg-transparent px-2 pb-0 pr-4 pt-0 font-hafferBold tracking-wide text-[12px] text-black placeholder-black hover:placeholder-black focus:placeholder-black"
                                name="selling-plan-select"
                                id="selling-plan-select"
                                value={selectedPlanId}
                                onChange={(e) => {
                                  setSelectedPlanId(e.target.value);
                                }}
                              >
                                {planOptions.map((option, optionIndex) => (
                                  <option
                                    key={`option-selling-plan-select-${optionIndex}`}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="mt-[10px] flex items-baseline whitespace-nowrap">
                            <span className="text-[11px] text-black">
                              {settings?.subscriptions?.cancelText ||
                                'Cancel anytime + membership perks.'}
                            </span>
                            {settings?.subscriptions?.subscriptionModal
                              ?.triggerCTALabel && (
                              <a
                                href="#"
                                className="ml-2 text-[11px] text-black underline"
                                onClick={toggleSubscriptionModal}
                              >
                                {
                                  settings?.subscriptions?.subscriptionModal
                                    ?.triggerCTALabel
                                }
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>
            
            <div className="block w-full">
              <div className="min-h-[48px] lg:min-h-[52px]">
                {variantIsAvailable && (
                  <button
                    type="button"
                    className="button button--checkout block flex flex h-[70px] w-full items-center justify-center md:px-4 md:text-[12px] lg:text-[16px]"
                    onClick={async () => {
                      try {
                        // custom attribute needed for Pack to properly serparate line items in the cart
                        const addItem =
                          selectedPlanId && isUs
                            ? {
                                merchandiseId: selectedVariant.id,
                                sellingPlanId: btoa(selectedPlanId),
                                quantity: 1,
                                attributes: [
                                  {
                                    key: '_sellingPlan',
                                    value: selectedPlanId,
                                  },
                                ],
                              }
                            : {
                                merchandiseId: selectedVariant.id,
                                quantity: 1,
                                attributes: [],
                              };
                        if (firstUpsellProduct) {
                          addItem.attributes.push({
                            key: '_upsellProduct',
                            value: firstUpsellProduct,
                          });
                        }
                        if (window.ShipScoutLineItemProperty) {
                          addItem.attributes.push({
                            key: '_sc',
                            value: window.ShipScoutLineItemProperty,
                          });
                        }
                        await cartAddItem(addItem, () => {
                          window.dispatchEvent(new CustomEvent('openSideCart'));
                          if (selectedPlanId && isUs) {
                            window.dataLayer.push({
                              event: 'PDP - Add_to_cart Subscribe & Save',
                            });
                          } else {
                            window.dataLayer.push({
                              event: 'PDP - Add_to_cart One Time',
                            });
                          }
                        });
                      } catch (error) {
                        console.error(error);
                        alert(
                          'Something whent wrong while adding to cart, please try again.'
                        );
                      }
                    }}
                  >

                  <span className="font-hafferBold tracking-wide">add to cart</span>
                    {country && comparePrice && (

                      <div>
                        {comparePriceText !== '' ? (
                          <span className="ml-8 font-hafferBold tracking-wide">
                            {comparePriceText}
                          </span>
                        ) : (
                          <span className="product-original-price ml-4 font-hafferBold tracking-wide line-through">
                            {comparePrice}
                          </span>
                        )}
                        <span className="sr-only">
                          Previous price {`${selectedPlan ? variantPrice : comparePrice}`}
                        </span>
                        {selectedPlan ? (
                          <>
                            <span className="ml-2 font-hafferBold tracking-wide">
                              {`${sellingPlanPrice}`}
                            </span>
                            <span className="sr-only">
                              Current price {`${sellingPlanPrice}`}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="ml-2 font-hafferBold tracking-wide">
                              {variantPrice}
                            </span>
                            <span className="sr-only">
                              Current price {`${variantPrice}`}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                    {country &&
                      selectedPlan &&
                      sellingPlanPrice &&
                      !comparePrice && (
                        <div>
                          <span className="product-original-price ml-4 font-hafferBold tracking-wide line-through">
                            {variantPrice}
                          </span>
                          <span className="ml-2 font-hafferBold tracking-wide">
                            {`${sellingPlanPrice}`}
                          </span>
                        </div>
                      )}
                    {country && !comparePrice && !selectedPlan && (
                      <>
                        {comparePriceText !== '' && (
                          <span className="ml-8 font-hafferBold tracking-wide">
                            {comparePriceText}
                          </span>
                        )}
                        <span
                          className={`${
                            comparePriceText !== '' ? 'ml-2' : 'ml-8'
                          } font-hafferBold tracking-wide`}
                        >
                          {variantPrice}
                        </span>
                      </>
                    )}
                  </button>
                )}
                {inventoryStatus.success && !variantIsAvailable && (
                  <div>
                    {isComingSoon !== 'true' ? (
                      <button
                        type="button"
                        disabled
                        className="button button--primary block flex w-full justify-center"
                      >
                        <span className="font-hafferBold tracking-wide">
                          {hasInventory ? 'unavailable' : 'out of stock'}
                        </span>
                      </button>
                    ) : (
                      <div>
                        <div className="button block flex w-full justify-center border-grey-7 bg-grey-7 text-black">
                          <span className="font-hafferBold tracking-wide">Coming Soon</span>
                        </div>
                        <ProductEmailSignup />
                      </div>
                    )}
                  </div>
                )}
                {isComingSoon !== 'true' &&
                  inventoryStatus.success &&
                  !hasInventory && (
                    <button
                      type="button"
                      className="button button--primary mt-5 block flex w-full justify-center"
                      onClick={toggleBackInStockModal}
                    >
                      <span className="font-hafferBold tracking-wide">
                        email me when available
                      </span>
                    </button>
                  )}
              </div>
             {country &&
                isUs &&
                inventoryStatus.success &&
                variantIsAvailable &&
                isComingSoon !== 'true' && (
                  <AfterPay
                    variantId={selectedVariant?.id}
                    price={variantPrice || 0}
                    currency={currencyCode}
                  />
                )}
            </div>
            {country && isUs && (
              <div className="mt-6 mb-[-1.5rem] sm:mt-8 sm:mb-auto flex flex-col sm:flex-row">
                <div className="productValueProp flex w-full lg:w-1/3">
                  {settings?.product?.productValueProps?.value_icon_1 && (
                    <span className="relative h-[30px] lg:h-[50px] w-[42px] lg:w-[50px] mr-0 lg:mr-2">
                      <img
                        className="w-[29px] lg:w-[33px]"
                        src={
                          settings?.product?.productValueProps?.value_icon_1
                            ?.src
                        }
                        alt={
                          settings?.product?.productValueProps?.value_text_1 ||
                          'free shipping on all subscriptions'
                        }
                        loading="lazy"
                        width="33"
                        height="33"
                      />
                    </span>
                  )}
                  <p className="text-small mt-[2px] lg:mt-auto mb-2 lg:mb-6">
                    {settings?.product?.productValueProps?.value_text_1 ||
                      'free shipping on all subscriptions'}
                  </p>
                </div>
                <div className="productValueProp flex w-full lg:w-1/3">
                  {settings?.product?.productValueProps?.value_icon_2 && (
                    <span className="relative h-[30px] lg:h-[50px] w-[42px] lg:w-[50px] mr-0 lg:mr-2">
                      <img
                        className="w-[29px] lg:w-[33px]"
                        src={
                          settings?.product?.productValueProps?.value_icon_2
                            ?.src
                        }
                        alt={
                          settings?.product?.productValueProps?.value_text_2 ||
                          'no hassle 30-day returns'
                        }
                        loading="lazy"
                        width="33"
                        height="33"
                      />
                    </span>
                  )}
                  <p className="text-small mt-[2px] lg:mt-auto mb-2 lg:mb-6">
                    {settings?.product?.productValueProps?.value_text_2 ||
                      'no hassle 30-day returns'}
                  </p>
                </div>
                <div className="productValueProp flex w-full lg:w-1/3">
                  {settings?.product?.productValueProps?.value_icon_3 && (
                    <span className="relative h-[30px] lg:h-[50px] w-[42px] lg:w-[50px] mr-0 lg:mr-2">
                      <img
                        className="w-[29px] lg:w-[33px]"
                        src={
                          settings?.product?.productValueProps?.value_icon_3
                            ?.src
                        }
                        alt={
                          settings?.product?.productValueProps?.value_text_3 ||
                          'rewards with every purchase'
                        }
                        loading="lazy"
                        width="33"
                        height="33"
                      />
                    </span>
                  )}
                  <p className="text-small mt-[2px] lg:mt-auto mb-2 lg:mb-6">
                    {settings?.product?.productValueProps?.value_text_3 ||
                      'rewards with every purchase'}
                  </p>
                </div>
              </div>
            )}
            <div className="pdp-buybox-video-wise">{videowise}</div>
            <div className="pdp-buybox-desktop-upsells hidden lg:block">
              {productupsells}
            </div>
          </div>
        </div>
      </div>
      <BackToTop scrollReference={buyboxRef} />
      {sections}
      <p className="mt-[44px] mb-[10px] text-center font-tiempos text-h4-m md:mt-[100px] md:mb-[40px] md:text-h4">
        Product Reviews &amp; Questions
      </p>

      <ReviewsWrapper selectedVariant={selectedVariant} product={product} />

      <BackInStockModal selectedVariant={selectedVariant} product={product} />

      <SubscriptionBenefitsModal
        modalContent={settings?.subscriptions?.subscriptionModal}
      />

      {inventoryStatus.success &&
        variantIsAvailable &&
        isComingSoon !== 'true' && (
          <ProductSticky
            settings={settings}
            scrollReference={productInfoRef}
            product={product}
            selectedPlan={selectedPlan}
            selectedPlanId={selectedPlanId}
            sellingPlanPrice={sellingPlanPrice}
            variantPrice={variantPrice}
            comparePrice={comparePrice}
          />
        )}
      <script
        type="application/ld+json"
        className="product-schema"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonSchema) }}
      />
    </div>
  );
}

const useGetInitialVariant = (product) => {
  const router = useRouter();

  if (!product) return null;

  const legacyVariantIdQuery = router.query.variant;

  if (legacyVariantIdQuery) {
    //! Attempt to find whaproduct variant that matches the query param
    const queriedVariant = product.variants.find(
      ({ legacyResourceId }) => legacyResourceId === legacyVariantIdQuery
    );

    if (queriedVariant) {
      return queriedVariant;
    }

    return product.variants[0];
  }
  //! We will find the first avaiable, and set that as the variant
  const firstAvailableVariant = product.variants.find(
    ({ availableForSale }) => availableForSale
  );

  if (firstAvailableVariant) {
    return firstAvailableVariant;
  }
  if (product?.variants?.length) {
    return product.variants[0];
  }
};

function ProductOptions({ activeVariant, product }) {
  const router = useRouter();

  if (!product || !product.options || product.variants.length <= 1) {
    return null;
  }

  const [selectedOptions, setSelectedOptions] = useState(
    activeVariant?.title.split(' / ').map((value) => value.trim())
  );

  useEffect(() => {

    if (selectedOptions) {
      const variantTitle = selectedOptions.join(' / ');

      const selectedVariant = product.variants.find(
        (variant) => variant.title === variantTitle
      );

      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            variant: selectedVariant.legacyResourceId,
          },
        },
        undefined,
        { shallow: true }
      );
    }
    if (
      typeof window !== 'undefined' &&
      window.hasOwnProperty('updateYotpoWidgets')
    ) {
      updateYotpoWidgets();
    }
  }, [selectedOptions]);

  return product.options.map((option, nameIndex) => {
    return (
      <div key={nameIndex} className="mb-6">
        <span className="text-small mb-1 font-hafferBold tracking-wide text-black">
          {' '}
          {option.name}{' '}
        </span>

        <div className="mx-auto flex w-full">
          {option.values.map((value, valueIndex) => {
            return (
              <button
                type="button"
                key={valueIndex}
                className={`w-1/6 border border py-2 px-1 font-hafferBold tracking-wide text-[12px] duration-200 hover:border-black hover:bg-grey-9 hover:text-black lg:text-base xl:text-[14px] ${
                  value === selectedOptions[nameIndex]
                    ? 'border-black bg-grey-9 text-black'
                    : 'border-grey-4 text-grey-3'
                }`}
                onClick={() => {
                  setSelectedOptions((curr) => {
                    const selectedOptions = [...curr];
                    selectedOptions[nameIndex] = value.trim();
                    return selectedOptions;
                  });
                }}
              >
                {value}
              </button>
            );
          })}
        </div>
      </div>
    );
  });
}